// Edits of a cost-structure tree (pure: every function returns a new tree, never mutates).
// Every edit respects canHold: a discipline holds either sub-disciplines or systems, a system
// holds nothing, the top level holds disciplines only. A refused edit throws TreeEditError.

import { canHold, findNode, walkTree } from "./structureRules.js";

export class TreeEditError extends Error {}

const clone = (x) => JSON.parse(JSON.stringify(x));
const kindOf = (n) => (n.sys ? "sys" : "disc");
const listOf = (tree, parentId) => (parentId ? findNode(tree, parentId).node.kids : tree);

let idCounter = 0;
export const newId = () => `u${Date.now().toString(36)}${(idCounter++).toString(36)}`;

export function blankNode(name, sys = false) {
  return { id: newId(), lib: null, name, en: "", cat: "", sys, on: true, hidden: false, split: true, sheet: "", loc: "", kids: [] };
}

const withFreshIds = (node) => ({ ...node, id: newId(), no: undefined, pno: undefined, kids: node.kids.map(withFreshIds) });

/** Lib ids already in the tree (a library row can be added once). */
export function usedLibIds(tree) {
  const used = new Set();
  walkTree(tree, (n) => n.lib && used.add(n.lib));
  return used;
}

/** What "+" on a row adds: a system after a system, otherwise a child of the kind the row already holds. */
export function plusTarget(tree, id) {
  const hit = findNode(tree, id);
  if (!hit) return null;
  if (hit.node.sys) return { parentId: hit.parent?.id ?? null, index: hit.index + 1, sys: true };
  const kids = hit.node.kids;
  return { parentId: id, index: kids.length, sys: kids.length ? kids[0].sys : false };
}

export function insertNode(tree, parentId, index, node) {
  const t = clone(tree);
  const parent = parentId ? findNode(t, parentId)?.node : null;
  if (parentId && !parent) throw new TreeEditError("მშობელი ვერ მოიძებნა");
  if (!canHold(parent, kindOf(node))) {
    throw new TreeEditError(
      !parent ? "ზედა დონეზე მხოლოდ დისციპლინაა" : parent.sys ? "სისტემა სხვა სტრიქონს ვერ შეიცავს" : "აქ ან ქვე-დისციპლინებია, ან სისტემები — ორივე ერთად არა"
    );
  }
  const list = parent ? parent.kids : t;
  list.splice(Math.min(Math.max(index, 0), list.length), 0, clone(node));
  if (parent) parent.open = true;
  return t;
}

/** Add a library row (with its preset children) under a parent; refuses rows already used. */
export function addFromLibrary(tree, libNode, parentId, index) {
  const used = usedLibIds(tree);
  if (used.has(libNode.lib)) throw new TreeEditError("ეს სტრიქონი უკვე სტრუქტურაშია");
  const strip = (n) => ({ ...n, kids: n.kids.filter((k) => !used.has(k.lib)).map(strip) });
  const list = listOf(tree, parentId);
  return insertNode(tree, parentId, index ?? list.length, strip(libNode));
}

/**
 * Add a library row where it fits relative to the selected row (as in the design): inside the
 * selection when the library row sits deeper, else after it on the same level, else inside it,
 * else (disciplines only) at the end. Returns { tree, message }.
 */
export function placeFromLibrary(tree, libNode, libDepth, selectedId) {
  const sel = selectedId ? findNode(tree, selectedId) : null;
  const kind = kindOf(libNode);
  const name = `„${libNode.name}“`;
  if (sel && sel.depth < libDepth && canHold(sel.node, kind))
    return { tree: addFromLibrary(tree, libNode, sel.node.id), message: `${name} დაემატა „${sel.node.name}“-ში.` };
  if (sel && canHold(sel.parent, kind))
    return {
      tree: addFromLibrary(tree, libNode, sel.parent?.id ?? null, sel.index + 1),
      message: `${name} დაემატა „${sel.node.name}“-ის შემდეგ, იმავე დონეზე.`,
    };
  if (sel && canHold(sel.node, kind))
    return { tree: addFromLibrary(tree, libNode, sel.node.id), message: `${name} დაემატა „${sel.node.name}“-ში.` };
  if (kind === "disc") return { tree: addFromLibrary(tree, libNode, null), message: `${name} დაემატა ბოლოში, როგორც დისციპლინა.` };
  throw new TreeEditError(`${name} სისტემაა — აირჩიეთ დისციპლინა, რომელშიც ჩაჯდება (სადაც ქვე-დისციპლინები არ არის).`);
}

export function updateNode(tree, id, patch) {
  const t = clone(tree);
  const hit = findNode(t, id);
  if (!hit) throw new TreeEditError("სტრიქონი ვერ მოიძებნა");
  Object.assign(hit.node, patch);
  return t;
}

/** Remove rows (and their branches). Ids whose ancestor is also removed are fine. */
export function removeNodes(tree, ids) {
  const drop = new Set(ids);
  const prune = (list) => list.filter((n) => !drop.has(n.id)).map((n) => ({ ...n, kids: prune(n.kids) }));
  return prune(tree);
}

export function duplicateNode(tree, id) {
  const hit = findNode(tree, id);
  if (!hit) throw new TreeEditError("სტრიქონი ვერ მოიძებნა");
  const copy = withFreshIds({ ...clone(hit.node), lib: null, name: `${hit.node.name} (ასლი)` });
  const strip = (n) => ({ ...n, lib: null, kids: n.kids.map(strip) });
  return insertNode(tree, hit.parent?.id ?? null, hit.index + 1, strip(copy));
}

/** Move a row up (-1) or down (+1) among its siblings. */
export function moveNode(tree, id, dir) {
  const t = clone(tree);
  const hit = findNode(t, id);
  if (!hit) throw new TreeEditError("სტრიქონი ვერ მოიძებნა");
  const to = hit.index + dir;
  if (to < 0 || to >= hit.list.length) throw new TreeEditError("უფრო შორს ვერ გადავა");
  const [node] = hit.list.splice(hit.index, 1);
  hit.list.splice(to, 0, node);
  return t;
}

/** Level down: becomes the last child of the row above it. */
export function indentNode(tree, id) {
  const hit = findNode(tree, id);
  if (!hit) throw new TreeEditError("სტრიქონი ვერ მოიძებნა");
  if (hit.index === 0) throw new TreeEditError("ზემოთ სტრიქონი არ არის");
  const target = hit.list[hit.index - 1];
  const moved = removeNodes(tree, [id]);
  return insertNode(moved, target.id, target.kids.length, hit.node);
}

/** Level up: becomes the sibling right after its parent. */
export function outdentNode(tree, id) {
  const hit = findNode(tree, id);
  if (!hit) throw new TreeEditError("სტრიქონი ვერ მოიძებნა");
  if (!hit.parent) throw new TreeEditError("უკვე ზედა დონეზეა");
  const parentHit = findNode(tree, hit.parent.id);
  const moved = removeNodes(tree, [id]);
  return insertNode(moved, parentHit.parent?.id ?? null, parentHit.index + 1, hit.node);
}

/** Move a row into another parent (null = top level), at the end. */
export function moveInto(tree, id, destParentId) {
  const hit = findNode(tree, id);
  if (!hit) throw new TreeEditError("სტრიქონი ვერ მოიძებნა");
  if (destParentId && (destParentId === id || findNode(hit.node.kids, destParentId)))
    throw new TreeEditError("სტრიქონს საკუთარ შიგნით ვერ გადაიტანთ");
  const moved = removeNodes(tree, [id]);
  return insertNode(moved, destParentId, listOf(moved, destParentId).length, hit.node);
}

/** Valid destinations for "move to…": disciplines that can hold the row, plus the top level. */
export function moveDestinations(tree, id) {
  const hit = findNode(tree, id);
  if (!hit) return [];
  const out = [];
  if (!hit.node.sys && hit.parent) out.push({ id: null, depth: 0 });
  walkTree(tree, (n, depth) => {
    if (n.id === id || findNode(hit.node.kids, n.id) || n.id === hit.parent?.id) return;
    if (canHold(n, kindOf(hit.node))) out.push({ id: n.id, depth });
  });
  return out;
}
